import React from 'react';

/**
 * Convert URLs in a text string into clickable <a> elements.
 * Returns an array of strings and React <a> elements.
 */
function linkifyText(text, keyPrefix = '') {
  if (!text) return [text];

  // Match http(s) URLs and www. URLs
  const urlRegex = /(https?:\/\/[^\s<>\"')\]]+|www\.[^\s<>\"')\]]+)/gi;
  const result = [];
  let lastIndex = 0;
  let match;
  let linkIndex = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }

    let url = match[0];
    // Strip trailing punctuation that's likely not part of the URL
    const trailingPunct = /[.,;:!?)]+$/;
    const trailingMatch = url.match(trailingPunct);
    let suffix = '';
    if (trailingMatch) {
      suffix = trailingMatch[0];
      url = url.substring(0, url.length - suffix.length);
    }

    const href = url.startsWith('www.') ? `https://${url}` : url;
    result.push(
      <a
        key={`${keyPrefix}link-${linkIndex}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#024B99', textDecoration: 'underline' }}
      >
        {url}
      </a>
    );
    if (suffix) result.push(suffix);

    lastIndex = match.index + match[0].length;
    linkIndex++;
  }

  // Add remaining text after last URL
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result.length > 0 ? result : [text];
}

/**
 * Parse inline markdown-style markers within a single text string.
 * Handles **bold**, *italic* (including nested), and URL auto-linking.
 * Returns an array of React elements and strings.
 */
function parseInlineFormatting(text, keyPrefix = '') {
  if (!text) return [text];

  const result = [];
  let remaining = text;
  let partIndex = 0;

  while (remaining.length > 0) {
    // Find the next marker: *** for bold+italic, ** for bold, * for italic
    const boldItalicIdx = remaining.indexOf('***');
    const boldIdx = remaining.indexOf('**');
    const italicIdx = remaining.indexOf('*');

    // No markers left — linkify any URLs in the remaining text
    if (italicIdx === -1) {
      result.push(...linkifyText(remaining, `${keyPrefix}t${partIndex}-`));
      break;
    }

    // Check for *** (bold+italic) first — must come before ** and * checks
    // boldItalicIdx === boldIdx means the first ** found is actually part of ***
    if (boldItalicIdx !== -1 && boldItalicIdx === boldIdx) {
      // Add text before the marker (with URL linkification)
      if (boldItalicIdx > 0) {
        result.push(...linkifyText(remaining.substring(0, boldItalicIdx), `${keyPrefix}pre${partIndex}-`));
      }

      // Find closing ***
      const closeIdx = remaining.indexOf('***', boldItalicIdx + 3);
      if (closeIdx === -1) {
        // No closing marker — treat as literal
        result.push(remaining.substring(0, boldItalicIdx + 3));
        remaining = remaining.substring(boldItalicIdx + 3);
        continue;
      }

      const innerText = remaining.substring(boldItalicIdx + 3, closeIdx);
      const innerElements = parseInlineFormatting(innerText, `${keyPrefix}bi${partIndex}-`);
      result.push(
        <strong key={`${keyPrefix}bolditalic-${partIndex}`}><em>{innerElements}</em></strong>
      );
      remaining = remaining.substring(closeIdx + 3);
      partIndex++;

    // Check if the first marker is ** (bold)
    } else if (boldIdx !== -1 && boldIdx <= italicIdx) {
      // Add text before the marker (with URL linkification)
      if (boldIdx > 0) {
        result.push(...linkifyText(remaining.substring(0, boldIdx), `${keyPrefix}pre${partIndex}-`));
      }

      // Find closing **
      const closeIdx = remaining.indexOf('**', boldIdx + 2);
      if (closeIdx === -1) {
        // No closing marker — treat as literal
        result.push(remaining.substring(0, boldIdx + 2));
        remaining = remaining.substring(boldIdx + 2);
        continue;
      }

      const innerText = remaining.substring(boldIdx + 2, closeIdx);
      const innerElements = parseInlineFormatting(innerText, `${keyPrefix}b${partIndex}-`);
      result.push(
        <strong key={`${keyPrefix}bold-${partIndex}`}>{innerElements}</strong>
      );
      remaining = remaining.substring(closeIdx + 2);
      partIndex++;
    } else {
      // Single * (italic) — but make sure it's not part of **
      if (italicIdx > 0) {
        result.push(...linkifyText(remaining.substring(0, italicIdx), `${keyPrefix}pre${partIndex}-`));
      }

      // Find closing *
      const closeIdx = remaining.indexOf('*', italicIdx + 1);
      if (closeIdx === -1) {
        // No closing marker — treat as literal
        result.push(remaining.substring(0, italicIdx + 1));
        remaining = remaining.substring(italicIdx + 1);
        continue;
      }

      const innerText = remaining.substring(italicIdx + 1, closeIdx);
      const innerElements = parseInlineFormatting(innerText, `${keyPrefix}i${partIndex}-`);
      result.push(
        <em key={`${keyPrefix}italic-${partIndex}`}>{innerElements}</em>
      );
      remaining = remaining.substring(closeIdx + 1);
      partIndex++;
    }
  }

  return result;
}

/**
 * Format a block of text (which may contain multiple lines) into React elements
 * with heading, bold, and italic support.
 *
 * - Lines starting with "## " become <h2>
 * - Lines starting with "### " become <h3>
 * - **text** becomes <strong>
 * - *text* becomes <em>
 * - Double newlines create paragraph breaks (handled by caller)
 *
 * @param {string} text - A paragraph/block of text (no image markers)
 * @param {string} keyPrefix - Key prefix for React elements
 * @param {object} [styleOverrides] - Optional style overrides for paragraph text
 * @returns {React.ReactNode[]} Array of React elements
 */
export function formatTextWithStyles(text, keyPrefix = '', styleOverrides = {}) {
  if (!text) return [text];

  // Split into lines to detect headings
  const lines = text.split('\n');
  const elements = [];
  let currentParagraphLines = [];

  const flushParagraph = () => {
    if (currentParagraphLines.length === 0) return;
    const paragraphText = currentParagraphLines.join('\n');
    if (paragraphText.trim()) {
      elements.push(
        <p key={`${keyPrefix}p-${elements.length}`} style={styleOverrides}>
          {parseInlineFormatting(paragraphText, `${keyPrefix}p${elements.length}-`)}
        </p>
      );
    }
    currentParagraphLines = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check for heading markers (### before ## since ### starts with ##)
    if (line.startsWith('### ')) {
      flushParagraph();
      const headingText = line.substring(4);
      elements.push(
        <h3 key={`${keyPrefix}h3-${i}`} style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          marginTop: '20px',
          marginBottom: '8px'
        }}>
          {parseInlineFormatting(headingText, `${keyPrefix}h3${i}-`)}
        </h3>
      );
    } else if (line.startsWith('## ')) {
      flushParagraph();
      const headingText = line.substring(3);
      elements.push(
        <h2 key={`${keyPrefix}h2-${i}`} style={{
          fontSize: '22px',
          fontWeight: '700',
          color: '#111827',
          marginTop: '24px',
          marginBottom: '10px'
        }}>
          {parseInlineFormatting(headingText, `${keyPrefix}h2${i}-`)}
        </h2>
      );
    } else if (line === '') {
      // Empty line — flush current paragraph (paragraph break)
      flushParagraph();
    } else {
      currentParagraphLines.push(line);
    }
  }

  // Flush any remaining paragraph
  flushParagraph();

  return elements;
}

/**
 * Apply one formatting toolbar action to plain text, using the markers formatTextWithStyles reads:
 * **bold**, *italic*, and "## " / "### " at the start of a line. Shared by every editor with the
 * formatting toolbar (src/components/shared/FormattingToolbar.jsx): articles and group
 * Introductions. Pure, so it is unit-tested.
 *
 * @param {string} text - the field's whole text
 * @param {number} start - selection start
 * @param {number} end - selection end
 * @param {'bold'|'italic'|'h2'|'h3'} type
 * @param {{ bold?: string, italic?: string }} [placeholders] - inserted when nothing is selected
 * @returns {{ text: string, cursor: number } | null} null for an unknown type
 */
export function applyTextFormatting(text, start, end, type, placeholders = {}) {
  const selectedText = text.substring(start, end);

  if (type === 'bold') {
    const wrapped = `**${selectedText || placeholders.bold || ''}**`;
    return {
      text: text.substring(0, start) + wrapped + text.substring(end),
      cursor: selectedText ? start + wrapped.length : start + 2
    };
  }
  if (type === 'italic') {
    const wrapped = `*${selectedText || placeholders.italic || ''}*`;
    return {
      text: text.substring(0, start) + wrapped + text.substring(end),
      cursor: selectedText ? start + wrapped.length : start + 1
    };
  }
  if (type === 'h2' || type === 'h3') {
    const prefix = type === 'h2' ? '## ' : '### ';
    // Find start of current line
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const lineText = text.substring(lineStart, end || start);
    // Remove existing heading markers if toggling
    const stripped = lineText.replace(/^#{2,3}\s/, '');
    const alreadyHasThis = type === 'h2' ? lineText.startsWith('## ') && !lineText.startsWith('### ') : lineText.startsWith('### ');
    const replacement = alreadyHasThis ? stripped : prefix + stripped;
    return {
      text: text.substring(0, lineStart) + replacement + text.substring(end || start),
      cursor: lineStart + replacement.length
    };
  }
  return null;
}

/**
 * Format a date/timestamp as a human-readable relative time string.
 * Requires a translation function `t` with keys:
 *   time_just_now, time_minutes_ago, time_hours_ago, time_days_ago
 * Falls back to a short locale date for anything older than 7 days.
 *
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {function} t - Translation function (e.g. from useTranslation('ui'))
 * @param {string} formattingLocale - the formatting locale, from useTranslation()/useFormattingLocale()
 * @returns {string} Formatted time-ago string
 */
export function formatTimeAgo(dateInput, t, formattingLocale) {
  const date = new Date(dateInput);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('time_just_now');
  if (diffMins < 60) return t('time_minutes_ago', { count: diffMins });
  if (diffHours < 24) return t('time_hours_ago', { count: diffHours });
  if (diffDays < 7) return t('time_days_ago', { count: diffDays });
  return date.toLocaleDateString(formattingLocale, { day: '2-digit', month: 'short' });
}

/**
 * Format a website URL as readable link text: bare hostname, scheme and leading
 * "www." stripped. Display only — the href a caller builds from the same raw value
 * is unaffected, so the link still opens the real URL, path and all.
 * Guards against schemeless input (hand-entered listings may omit it; a scheme is
 * added before parsing so `new URL()` doesn't throw for that reason) and falls back
 * to the older scheme-stripped raw text for anything else unparseable.
 *
 * @param {string} url - Raw website value, with or without a scheme
 * @returns {string} Display text (bare hostname, no scheme, no leading www.)
 */
export function formatWebsiteDisplay(url) {
  if (!url) return '';
  const withScheme = url.startsWith('http') ? url : `https://${url}`;
  try {
    const hostname = new URL(withScheme).hostname;
    return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  } catch (e) {
    return url.replace(/^https?:\/\//, '');
  }
}
